import type { AppProps } from 'next/app';
import { appWithTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useEffect } from 'react';
import { Inter, Poppins } from 'next/font/google';
import nextI18NextConfig from '../../next-i18next.config';
import { ThemeProvider } from '../providers/ThemeProvider';
import '../styles/globals.css';
import '../styles/rtl.css'; // Import RTL styles

// Suppress fetchPriority warning in production
if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
  const originalConsoleError = window.console.error;
  window.console.error = (...args) => {
    if (typeof args[0] === 'string' && args[0].includes('fetchPriority')) {
      return;
    }
    originalConsoleError.apply(console, args);
  };
}

type PageProps = {
  _nextI18Next?: {
    initialI18nStore?: {
      [locale: string]: {
        [namespace: string]: {
          [key: string]: any;
        };
      };
    };
    initialLocale?: string;
    ns?: string[];
    userConfig?: any;
  };
};

// Cấu hình font
const inter = Inter({ 
  subsets: ['latin'], 
  variable: '--font-inter',
  display: 'swap',
});

const poppins = Poppins({
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
  variable: '--font-poppins',
  display: 'swap',
});

function MyApp({ Component, pageProps }: AppProps<PageProps>) {
  const router = useRouter();
  const { locale } = router;

  // Cập nhật ngôn ngữ cho HTML
  useEffect(() => {
    if (locale) {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <div className={`${inter.variable} ${poppins.variable} font-sans min-h-screen bg-background text-foreground`}>
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta name="description" content={pageProps._nextI18Next?.initialI18nStore?.[locale || 'en']?.common?.heroDescription || 'Download videos from various platforms'} />
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        </Head>
        <Component {...pageProps} />
      </div>
    </ThemeProvider>
  );
}

// Sử dụng appWithTranslation với cấu hình đã được import
const MyAppWithI18n = appWithTranslation(MyApp, nextI18NextConfig);

export default MyAppWithI18n;
